import React, { useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import { useHistory, useLocation } from 'react-router-dom';
import { Loader, Message } from 'semantic-ui-react';
import { convertOidcUserToLoginInfo } from '../Utils/OidcAuth';
import { LoginInfo, APIRecordLogin } from '../Utils/Login';
import { setCurrentUser } from '../Utils/ApiConfig';

interface OidcCallbackProps {
    setLoginInfo: (loginInfo: LoginInfo) => void;
}

export default function OidcCallback({ setLoginInfo }: OidcCallbackProps) {
    const auth = useAuth();
    const history = useHistory();
    const location = useLocation();

    useEffect(() => {
        if (auth.isLoading) {
            return;
        }

        if (auth.error) {
            console.error('OIDC Authentication error:', auth.error);
            // Redirect to login with error
            history.push('/?error=auth_failed');
            return;
        }

        if (auth.isAuthenticated && auth.user) {
            try {
                // Set the current user in the API config for JWT token usage
                setCurrentUser(auth.user);

                // Convert OIDC user to LoginInfo format
                const loginInfo = convertOidcUserToLoginInfo(auth.user);
                setLoginInfo(loginInfo);

                // Record the login in the backend
                APIRecordLogin((statusCode) => {
                    if (statusCode !== 200) {
                        console.warn('Failed to record login, status code:', statusCode);
                    }
                });

                // Check if there's a destination in the state parameter
                let dest: string | null = null;

                // Try to get destination from state parameter
                if (auth.user?.state) {
                    try {
                        const state = typeof auth.user.state === 'string'
                            ? JSON.parse(auth.user.state)
                            : auth.user.state;
                        dest = state.dest || null;
                    } catch (e) {
                        console.warn('Could not parse state parameter:', e);
                    }
                }

                // Fallback to query parameter for backward compatibility
                if (!dest) {
                    const params = new URLSearchParams(location.search);
                    dest = params.get('dest');
                }

                if (dest) {
                    // Decode and redirect to destination
                    try {
                        const decodedDest = decodeURIComponent(dest);
                        history.push(decodedDest);
                    } catch (e) {
                        console.error('Invalid destination URL:', dest);
                        history.push('/');
                    }
                } else {
                    // Redirect to home page
                    history.push('/');
                }

            } catch (error) {
                console.error('Error converting OIDC user:', error);
                history.push('/?error=user_conversion_failed');
            }
        } else {
            // If not authenticated and not loading, redirect to login
            history.push('/');
        }
    }, [auth.isLoading, auth.error, auth.isAuthenticated, auth.user, setLoginInfo, history, location]);

    if (auth.isLoading) {
        return (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
                <Loader active size="large">
                    Completing authentication...
                </Loader>
            </div>
        );
    }

    if (auth.error) {
        return (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
                <Message negative>
                    <Message.Header>Authentication Error</Message.Header>
                    <p>An error occurred during authentication. Please try again.</p>
                    <p>Error: {auth.error.message}</p>
                </Message>
            </div>
        );
    }

    return (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
            <Loader active size="large">
                Processing authentication...
            </Loader>
        </div>
    );
}
